import React from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import { Activity } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './ReferenceLineTopLabel';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';

function Envelope({ title, curve, baseSpeedRpm, idSuffix }) {
  const zoom = useChartZoom();
  return (
    <div>
      <ChartZoomHeader caption={title} isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId={`zoom-reset-envelope-${idSuffix}`} />
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart
          data={curve} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 8, bottom: 8 }}
          onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="rpm" type="number" domain={zoom.xDomain} allowDataOverflow
            tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Motor Speed (RPM)', position: 'insideBottom', offset: -4, style: AXIS_LABEL_STYLE }}
            tickFormatter={(v) => Math.round(v).toLocaleString()}
          />
          <YAxis
            yAxisId="torque" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Torque (Nm)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} width={56}
          />
          <YAxis
            yAxisId="power" orientation="right" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false}
            label={{ value: 'Power (kW)', angle: 90, position: 'insideRight', style: AXIS_LABEL_STYLE }} width={56}
          />
          <Tooltip
            contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={(v) => `${Math.round(v).toLocaleString()} RPM`}
            formatter={(v) => Number(v).toFixed(1)}
          />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {baseSpeedRpm && (
            <ReferenceLine x={baseSpeedRpm} yAxisId="torque" stroke={CHART_AXIS} strokeDasharray="4 4"
              label={<ReferenceLineTopLabel text="Base Speed" anchor={referenceLabelAnchorFor(baseSpeedRpm, zoom.xDomain, curve, 'rpm')} />} />
          )}
          <Line yAxisId="torque" type="monotone" dataKey="peak_torque_nm" name="Peak Torque (Nm)" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
          <Line yAxisId="torque" type="monotone" dataKey="continuous_torque_nm" name="Continuous Torque (Nm)" stroke={SERIES_COLORS[6]} {...LINE_PROPS} />
          <Line yAxisId="power" type="monotone" dataKey="peak_power_kw" name="Peak Power (kW)" stroke={SERIES_COLORS[1]} {...LINE_PROPS} />
          {zoom.isSelecting && (
            <ReferenceArea yAxisId="torque" x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Motor Graphs (§4.3 / plots 8-11): the motor's torque-speed and power-speed envelope,
 * shown both with-margin (the curve the motor is actually built to) and without-margin
 * (the bare requirement) — both use the Peak (30s) torque/speed/power values as the
 * defining envelope, per spec.
 */
const MotorEnvelopeChart = ({ curveWithMargin, curveWithoutMargin, baseSpeedRpm }) => {
  if (!curveWithMargin || !curveWithoutMargin) return null;

  return (
    <ChartCard
      icon={Activity}
      title="Motor Graphs"
      description="Torque-speed and power-speed envelope, with and without Motor Safety Margin."
      testId="chart-motor-envelope"
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <Envelope title="With Margin" curve={curveWithMargin} baseSpeedRpm={baseSpeedRpm} idSuffix="with-margin" />
        <Envelope title="Without Margin" curve={curveWithoutMargin} baseSpeedRpm={null} idSuffix="without-margin" />
      </div>
    </ChartCard>
  );
};

export default MotorEnvelopeChart;
